import api from './api';

// --- Interfaces ---

export interface Icp {
  id: string;
  name: string;
  description?: string;
  product?: string;
  industry?: string;
  companySize?: string;
  geography?: string;
  signals?: string;
  painPoints?: string;
  rationale?: string;
  priority: number;
  status: string;
  aiGenerated: boolean;
  rawOutput?: string;
  // Auto-Defaults für die Icebreaker-Generierung (channels/autoSources kommasepariert).
  icebreakerTone?: string;
  icebreakerLanguage?: string;
  icebreakerGoal?: string;
  icebreakerChannels?: string;
  icebreakerAutoSources?: string;
  userId: string;
  tenantId?: string;
  createdAt: string;
  updatedAt: string;
  _count?: { personas: number; offers: number };
  personas?: Persona[];
  offers?: Offer[];
}

export interface Persona {
  id: string;
  name: string;
  description?: string;
  icpId?: string;
  seniority?: string;
  buyingRole?: string;
  responsibilities?: string;
  goals?: string;
  painPoints?: string;
  objections?: string;
  priority: number;
  status: string;
  aiGenerated: boolean;
  rawOutput?: string;
  userId: string;
  tenantId?: string;
  createdAt: string;
  updatedAt: string;
  icp?: { id: string; name: string } | null;
}

export interface Offer {
  id: string;
  name: string;
  description?: string;
  icpId?: string;
  oneLiner?: string;
  valueProps?: string;
  outcomes?: string;
  proofPoints?: string;
  status: string;
  aiGenerated: boolean;
  rawOutput?: string;
  userId: string;
  tenantId?: string;
  createdAt: string;
  updatedAt: string;
  icp?: { id: string; name: string } | null;
}

export interface GenerateIcpInput {
  product: string;
  existingCustomers?: string;
  pricing?: string;
  constraints?: string;
}

export interface GeneratePersonaInput {
  icpId?: string;
  product?: string;
  context?: string;
}

export interface GenerateOfferInput {
  product: string;
  icpId?: string;
  features?: string;
  context?: string;
}

// --- Service ---

export interface GtmOverview {
  icps: number;
  personas: number;
  offers: number;
  salesCampaigns: number;
  signalCampaigns: number;
  icpCampaigns: number;
  leads: number;
  unhandledReplies: number;
}

class GtmService {
  async getOverview(): Promise<GtmOverview> {
    const response = await api.get('/gtm/overview');
    return response.data;
  }

  // ICPs
  async getIcps(status?: string): Promise<Icp[]> {
    const response = await api.get('/gtm/icps', { params: status ? { status } : {} });
    return response.data;
  }
  async getIcp(id: string): Promise<Icp> {
    const response = await api.get(`/gtm/icps/${id}`);
    return response.data;
  }
  async createIcp(input: Partial<Icp>): Promise<Icp> {
    const response = await api.post('/gtm/icps', input);
    return response.data;
  }
  async generateIcp(input: GenerateIcpInput): Promise<{ icp: Icp; provider: string; model: string }> {
    const response = await api.post('/gtm/icps/generate', input);
    return response.data;
  }
  async createIcpFromStrategy(strategyId: string): Promise<{ icp: Icp; alreadyLinked: boolean }> {
    const response = await api.post(`/gtm/icps/from-strategy/${strategyId}`);
    return response.data;
  }
  async updateIcp(id: string, input: Partial<Icp>): Promise<Icp> {
    const response = await api.put(`/gtm/icps/${id}`, input);
    return response.data;
  }
  async deleteIcp(id: string): Promise<void> {
    await api.delete(`/gtm/icps/${id}`);
  }

  // Personas
  async getPersonas(filters?: { status?: string; icpId?: string }): Promise<Persona[]> {
    const response = await api.get('/gtm/personas', { params: filters || {} });
    return response.data;
  }
  async createPersona(input: Partial<Persona>): Promise<Persona> {
    const response = await api.post('/gtm/personas', input);
    return response.data;
  }
  async generatePersona(
    input: GeneratePersonaInput
  ): Promise<{ persona: Persona; provider: string; model: string }> {
    const response = await api.post('/gtm/personas/generate', input);
    return response.data;
  }
  async updatePersona(id: string, input: Partial<Persona>): Promise<Persona> {
    const response = await api.put(`/gtm/personas/${id}`, input);
    return response.data;
  }
  async deletePersona(id: string): Promise<void> {
    await api.delete(`/gtm/personas/${id}`);
  }

  // Offers
  async getOffers(filters?: { status?: string; icpId?: string }): Promise<Offer[]> {
    const response = await api.get('/gtm/offers', { params: filters || {} });
    return response.data;
  }
  async createOffer(input: Partial<Offer>): Promise<Offer> {
    const response = await api.post('/gtm/offers', input);
    return response.data;
  }
  async generateOffer(
    input: GenerateOfferInput
  ): Promise<{ offer: Offer; provider: string; model: string }> {
    const response = await api.post('/gtm/offers/generate', input);
    return response.data;
  }
  async updateOffer(id: string, input: Partial<Offer>): Promise<Offer> {
    const response = await api.put(`/gtm/offers/${id}`, input);
    return response.data;
  }
  async deleteOffer(id: string): Promise<void> {
    await api.delete(`/gtm/offers/${id}`);
  }
}

const gtmService = new GtmService();
export default gtmService;
